import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import styles from './Chat.module.scss';

interface MarkdownMessageProps {
  content: string;
  streaming?: boolean;
}

// Custom schema that preserves rehype-highlight output and component data attributes.
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'span'],
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span || []), 'className'],
    code: [...(defaultSchema.attributes?.code || []), 'className', 'data-inline'],
    pre: [...(defaultSchema.attributes?.pre || []), 'data-lang'],
  },
};

// Renders AI/system message content as Markdown with GFM + code highlighting.
// HTML is skipped for safety; rehype-sanitize provides defense-in-depth.
// Provide a partial components map; each function receives specific props with node info.
// Use a Partial<Components> to avoid strict prop type mismatch for specialized list items.
const components: Partial<Components> = {
  a: (anchorProps: any) => {
    const { href, children, ...rest } = anchorProps;
    return (
      <a
        href={href || ''}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.markdownLink}
        {...rest}
      >
        {children}
      </a>
    );
  },
  code: (codeProps: any) => {
    const { inline, className, children, ...rest } = codeProps;
    const languageMatch = /language-(\w+)/.exec(className || '');
    const lang = languageMatch ? languageMatch[1] : undefined;
    const isInline = inline ?? !className;
    if (isInline) {
      return (
        <code className={styles.inlineCode} {...rest} data-inline="true">
          {children}
        </code>
      );
    }
    return (
      <pre className={styles.codeBlock} data-lang={lang}>
        <code className={className} {...rest}>{children}</code>
      </pre>
    );
  },
  table: (tableProps: any) => {
    const { children, ...rest } = tableProps;
    return (
      <div className={styles.tableWrapper}>
        <table {...rest}>{children}</table>
      </div>
    );
  },
  blockquote: (bqProps: any) => {
    const { children, ...rest } = bqProps;
    return (
      <blockquote className={styles.blockQuote} {...rest}>{children}</blockquote>
    );
  },
  // List items use default rendering; styled via CSS selectors for consistency.
};

// Fallback linkifier for plain URLs (that are not already markdown links).
function linkifyPlainUrls(source: string): string {
  // Avoid touching fenced code blocks by splitting; simple heuristic.
  // This won't handle nested fences but adequate for chat context.
  const segments = source.split(/```/);
  return segments
    .map((seg, idx) => {
      if (idx % 2 === 1) return seg; // inside code fence, leave untouched
      // Replace raw URLs not already part of markdown link syntax.
      return seg.replace(/(?<!\]\()((https?:\/\/|www\.)[^\s<]+[^\s<\.,])/g, (match) => {
        // If already inside parentheses following ], skip (negative lookbehind above attempts this).
        const url = match.startsWith('www.') ? `https://${match}` : match;
        return `[${match}](${url})`;
      });
    })
    .join('```');
}

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ content, streaming }) => {
  const prepared = linkifyPlainUrls(content);
  return (
    <div className={styles.markdownBody} data-streaming={streaming ? 'true' : 'false'}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight as any, [rehypeSanitize, sanitizeSchema]]}
        skipHtml
        components={components}
      >
        {prepared}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;